<template>
  <div>
    <v-text-field
      v-bind="computedProps"
      solo
      flat
      :placeholder="phtext"
      @dblclick="setCurrentDate(0)"
      :tabindex="readonly ? -1 : 0"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      @keydown.enter="onKeydownEnter"
      @keydown.tab="onKeydownTab"
      @keydown.esc="onKeydownEsc"
    >
      <template v-slot:append>
        <v-menu
          v-show="!readonly"
          v-model="isShowCalendar"
          :close-on-content-click="false"
          origin="top left">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-show="!readonly"
              v-on="on"
              v-bind="attrs"
              icon
              color="primary"
              dark
              elevation="0"
              small
              tabindex=-1
              class="pa-0"
              @click="onIconClick">
              <v-icon>mdi-calendar</v-icon>
            </v-btn>
          </template>
          <v-date-picker
            :no-title="true"
            elevation="8"
            locale="jp-ja"
            :day-format="date => new Date(date).getDate()"
            :max="calendarMax"
            :min="calendarMin"
            @input="onCalendarInput"
            v-model="calendarDate">
            <v-spacer></v-spacer>
            <dd-default-button
              dark
              dense
              @click="onCalendarClose">
              閉じる
            </dd-default-button>
            <v-spacer></v-spacer>
            <dd-default-button
              dark
              dense
              @click="setCurrentDate(1)">
              当日
            </dd-default-button>
            <v-spacer></v-spacer>
          </v-date-picker>
        </v-menu>
      </template>
    </v-text-field>
  </div>
</template>
<script>
import DdDefaultButton from '@/components/DdDefaultButton'
import controls from '@/rahmen/controls'
import complements from '@/rahmen/complement'
import validators from '@/rahmen/validators'
import inputMixin from '@/mixins/inputMixins'
import { VTextField } from 'vuetify/lib'
import dayjs from 'dayjs'
import date from '@/ddv2.library/date'

import customParseFormat from 'dayjs/plugin/customParseFormat'

/**
 * @file datatec用日時入力
 * @namespace components
 */

/**
 * @vue-prop {Boolean} [required=false] - 必須有無
 * @vue-prop {String} [max='']- 許容最大値(デフォルト:2999/12/31)
 * @vue-prop {String} [min=''] - 許容最小値(デフォルト:2000/01/01)
 * @vue-prop {String} [errorMessages=''] - エラーメッセージ
 * @see https://vuetifyjs.com/ja/api/v-text-field/
 * @example
  <wl-date-box
    label=""
    v-model="dateValue"
    ref="dateValue"
    displayFormat="MM/DD"
    max="2099/12/31 23:59:59"
    min="2000/01/01 00:00:00"/>
 */
export default {
  name: 'DdSpreadSheetDateBox',
  extends: VTextField,
  components: {
    DdDefaultButton
  },
  mixins: [inputMixin],
  props: {
    /**
     * 必須指定
     */
    required: Boolean,
    /**
     * 入力可能最大値
     */
    max: String,
    /**
     * 入力可能最小値
     */
    min: String,
    /**
     * 表示エラーメッセージ
     */
    errorMessages: String,
    /**
     * 表示フォーマット(dayjs準拠)
     */
    displayFormat: String,
    /**
     * 個別バリデイト
     */
    customValidate: {
      type: Function,
      default: () => { return { isComplete: true, message: null } }
    }
  },
  data: () => ({
    invalidMessage: '',
    defaultFormat: 'YYYY/MM/DD',
    defaultMax: '2999/12/31',
    defaultMin: '2000/01/01',
    displayValue: '',
    isShowCalendar: false,
    calendarDate: null,
    inputValue: null
  }),
  created () {
  },
  mounted () {
    dayjs.extend(customParseFormat)

    this.invalidMessage = this.errorMessages
    this.inputValue = this.value
    this.displayValue = this.displayDateFormat(this.value)
  },
  computed: {
    computedProps () {
      return {
        ...this.$props,
        value: this.displayValue,
        maxlength: this.length,
        class: 'dtInputPadding0 ' + this.class + (this.required ? ' required-item' : ''),
        hideDetails: 'auto',
        dense: true,
        error: this.hasError,
        errorMessages: this.invalidMessage
      }
    },
    calendarMax () {
      if (this.max) {
        return dayjs(this.max).format('YYYY-MM-DD')
      }
      return undefined
    },
    calendarMin () {
      if (this.min) {
        return dayjs(this.min).format('YYYY-MM-DD')
      }
      return undefined
    },
    hasError () {
      return this.invalidMessage !== undefined
    },
    phtext () {
      return !this.readonly ? (this.placeholder !== null && this.placeholder !== undefined ? this.placeholder : (this.displayFormat ?? this.defaultFormat)) : ''
    }
  },
  watch: {
    value: function (n, o) {
      this.$emit('change', n)
      this.$set(this, 'displayValue', this.displayDateFormat(n))
      this.inputValue = n
      this[controls.VALIDATE_METHOD]()
      if (!this.hasError) {
        // this[controls.FORMAT_METHOD]()
      }
    }
  },
  methods: {
    onInput: function (e) {
      this.inputValue = e
    },
    onBlur: function (e) {
      // console.log('onBlur')
      this.complement()
      this.$emit('blur', e)
    },
    onFocus: function (e) {
      // console.log('onFocus')
      const val = this.lazyValue
      if (this.lazyValue !== null && this.lazyValue !== '' && this.lazyValue !== undefined) {
        this.displayValue = val
      }
      const elem = this.$el.querySelector('input')
      this.$nextTick().then(() => {
        if (elem) { elem.select() }

        /**
         * フォーカス設定
         * @event focus
         * @type {Event}
         */
        this.$emit('focus', e)
      })
    },
    onKeydownEnter: function (e) {
      // console.log('onKeydownEnter')
      this.$emit('enter', e)
    },
    onKeydownTab: function (e) {
      // console.log('onKeydownTab')
      this.$emit('tab', e)
    },
    onKeydownEsc: function (e) {
      // console.log('onKeydownEsc')
      this.$emit('esc', e)
    },
    onIconClick: function (e) {
      // console.log('onIconClick', e, this.lazyValue)
      if (this.lazyValue !== null && this.lazyValue !== '' && this.lazyValue !== undefined) {
        var s = (complements.parseDate(this.lazyValue)).split('/')
        var d = new Date(s)
        if (isNaN(d.getDate())) {
          // 日付でない場合は処理日を設定
          this.calendarDate = dayjs(date.getCurrentDate()).format('YYYY-MM-DD')
        } else {
          this.calendarDate = dayjs(complements.parseDate(this.lazyValue)).format('YYYY-MM-DD')
        }
      } else {
        this.calendarDate = dayjs(date.getCurrentDate()).format('YYYY-MM-DD')
      }
    },
    onCalendarInput: function (e) {
      // console.log('onCalendarInput e', e)
      const val = dayjs(e).format('YYYY/MM/DD')
      // console.log('onCalendarInput val', val)
      this.$set(this, 'displayValue', this.displayDateFormat(val))
      const changeFlg = this.lazyValue !== val
      /**
       * カレンダー選択値input
       * @event input
       * @type {Event}
       */
      if (!this.readonly) this.$emit('input', val)
      this.inputValue = val
      if (changeFlg) this.$emit('change')
      this.isShowCalendar = false
    },
    onCalendarClose: function (e) {
      // console.log('onCalendarClose e', e)
      this.isShowCalendar = false
      this.$emit('close')
    },
    complement: function () {
      // console.log('complement this.inputValue', this.inputValue)
      const val = this.inputValue ? complements.parseDate(this.inputValue) : this.inputValue
      this.$set(this, 'displayValue', this.displayDateFormat(val))
      /**
       * 補完値input
       * @event input
       * @type {Event}
       */
      if (!this.readonly) this.$emit('input', val)
      this.inputValue = val
    },
    [controls.VALIDATE_METHOD]: function () {
      // console.log('VALIDATE_METHOD', this.lazyValue)
      const target = {
        value: this.lazyValue,
        max: this.max === undefined ? this.defaultMax : this.max,
        min: this.min === undefined ? this.defaultMin : this.min
      }
      const prevErrorMessage = this.invalidMessage
      this.invalidMessage = undefined
      const validatorList = controls.getInputTypeValidators(controls.InputTypes.Date)

      if (this.required) { validatorList.unshift(validators.requireValidate) }
      if (this.appendValidators) { validatorList.push(...this.appendValidators) }

      for (let i = 0; i < validatorList.length; i++) {
        const f = validatorList[i]
        const ret = f(target)
        if (ret !== undefined) {
          this.invalidMessage = ret
          if (prevErrorMessage !== this.invalidMessage) this.$emit('update:error', true)
          return
        }
      }

      if (this.lazyValue) {
        const customCheck = this.customValidate()
        if (!customCheck.isComplete) {
          if (customCheck.message) this.invalidMessage = customCheck.message
          if (prevErrorMessage !== this.invalidMessage) this.$emit('update:error', true)
          return
        }
      }
      if (prevErrorMessage) this.$emit('update:error', false)
    },
    [controls.FORMAT_METHOD]: function () {
    },
    setCurrentDate: function (overwrite) {
      if (this.readonly) return

      if (this.displayValue === undefined || this.displayValue === '' || overwrite === 1) {
        this.displayValue = date.getCurrentDate()
        if (!this.readonly) this.$emit('input', this.displayValue)
        this[controls.VALIDATE_METHOD]()
      }
      this.isShowCalendar = false
    },
    displayDateFormat: function (date) {
      // console.log('displayDateFormat date', date)
      if (date === null || date === undefined || date === '' || !dayjs(date, 'YYYY/MM/DD', true).isValid()) return date

      const fm = this.displayFormat ?? this.defaultFormat
      return dayjs(date, 'YYYY/MM/DD', true).format(fm)
    },
    [controls.CLEAR_INVALID_METHOD]: function () {
      // console.log(controls.CLEAR_INVALID_METHOD)
      if (this.invalidMessage) this.$emit('update:error', false)
      this.invalidMessage = undefined
    },
    [controls.SET_INVALID_METHOD]: function (message) {
      // console.log(controls.SET_INVALID_METHOD, message)
      const prevMessage = this.invalidMessage
      this.invalidMessage = message
      if (prevMessage !== message) this.$emit('update:error', true)
    }
  }
}
</script>
<style>
.dtInputPadding0.v-text-field input {
  padding-right: 0 !important;
}
.v-application--is-ltr .dtInputPadding0.v-text-field .v-input__append-inner {
  padding-left: 0 !important;
}
</style>
